import { useCallback } from 'react'
import { useEffectOnce } from 'react-use'

import { useRemoteCollection, useErrorNotification, useSuccessNotification } from 'civic-champs-shared/api/hooks'
import { SuperEvent } from 'super-events/types'
import useGetSuperEvents from './useGetSuperEvents'
import useCreateOrUpdateSuperEvent from './useCreateOrUpdateSuperEvent'
import { Action, BulkAction } from 'civic-champs-shared/api/hooks/useRemoteCollection'
import useDeleteSuperEvents from './useDeleteSuperEvent'
import { map } from 'lodash'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { AddEditSuperEventPrompt, AddEditSuperEventResult } from 'super-events/components/AddEditSuperEventPrompt'

export interface SuperEventCollectionData {
  superEvents: SuperEvent[]
  loading: boolean
  busy: boolean
}

export interface SuperEventCollectionOperations {
  refresh: () => Promise<void>
  add: () => Promise<SuccessResult>
  edit: (toEdit: SuperEvent) => Promise<SuccessResult>
  bulkDelete: (toRemove: SuperEvent[]) => Promise<SuccessResult>
}

export interface SuccessResult {
  success: boolean
}

const useAdd = (eagerAdd: Action<SuperEvent>): [() => Promise<SuccessResult>, boolean] => {
  const showPrompt = useShowPrompt(AddEditSuperEventPrompt)
  const [createSuperEvent, { loading: adding }] = useCreateOrUpdateSuperEvent()
  const showError = useErrorNotification()
  const showSuccess = useSuccessNotification()

  const addSuperEvent = useCallback(async () => {
    try {
      const result = (await showPrompt({})) as AddEditSuperEventResult

      if (!result.proceed) return { success: false }

      const newEvent = await createSuperEvent(result.superEvent)
      eagerAdd(newEvent)
      showSuccess('Super Event created!')
      return { success: true }
    } catch (error) {
      showError('Super Event failed', error)
      return { success: false }
    }
  }, [eagerAdd, showPrompt, createSuperEvent, showError, showSuccess])

  return [addSuperEvent, adding]
}

const useEdit = (eagerReplace: Action<SuperEvent>): [(superEvent: SuperEvent) => Promise<SuccessResult>, boolean] => {
  const showPrompt = useShowPrompt(AddEditSuperEventPrompt)
  const [updateSuperEvent, { loading: editing }] = useCreateOrUpdateSuperEvent()
  const showError = useErrorNotification()
  const showSuccess = useSuccessNotification()

  const edit = useCallback(
    async (superEvent: SuperEvent) => {
      try {
        const result = (await showPrompt({ superEvent })) as AddEditSuperEventResult

        if (!result.proceed) return { success: false }

        const newEvent = await updateSuperEvent(result.superEvent)
        eagerReplace(newEvent)
        showSuccess('Super Event updated!')
        return { success: true }
      } catch (error) {
        showError('Update Super Event failed', error)
        return { success: false }
      }
    },
    [eagerReplace, showPrompt, updateSuperEvent, showError, showSuccess],
  )

  return [edit, editing]
}

const useBulkDelete = (
  eagerRemoveMany: BulkAction<SuperEvent>,
): [(superEvents: SuperEvent[]) => Promise<SuccessResult>, boolean] => {
  const [deleteSuperEvents, deleting] = useDeleteSuperEvents()
  const showError = useErrorNotification()
  const showSuccess = useSuccessNotification()

  const bulkDelete = useCallback(
    async (superEvents: SuperEvent[]): Promise<SuccessResult> => {
      const undo = eagerRemoveMany(superEvents)
      try {
        const ids = map(superEvents, superEvent => superEvent.id) as number[]
        await deleteSuperEvents(ids)
        showSuccess('Super Events deleted')
        return { success: true }
      } catch (error) {
        undo()
        showError('failed to delete Super Events', error)
        return { success: false }
      }
    },
    [deleteSuperEvents, showSuccess, showError, eagerRemoveMany],
  )

  return [bulkDelete, deleting]
}

const useSuperEventCollection = (): [SuperEventCollectionData, SuperEventCollectionOperations] => {
  const [superEvents, operations] = useRemoteCollection<SuperEvent>()
  const [getSuperEvents, { called, loading }] = useGetSuperEvents()

  const { syncCollection, eagerAdd, eagerReplace, eagerRemoveMany } = operations

  const refresh = useCallback(async () => {
    const updatedSuperEvents = await getSuperEvents()
    syncCollection(updatedSuperEvents)
  }, [getSuperEvents, syncCollection])

  useEffectOnce(() => {
    refresh()
  })

  const [add, adding] = useAdd(eagerAdd)
  const [edit, editing] = useEdit(eagerReplace)
  const [bulkDelete, deleting] = useBulkDelete(eagerRemoveMany)

  return [
    {
      superEvents,
      loading: !called || loading,
      busy: loading || adding || editing || deleting,
    },
    {
      refresh,
      add,
      edit,
      bulkDelete,
    },
  ]
}

export default useSuperEventCollection
